import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';

import ConvertMembershipFormModal from 'src/features/membership/components/forms/ConvertMembershipFormModal';
import DeleteMembershipFormModal from 'src/features/membership/components/forms/DeleteMembershipFormModal';
import UpdateMembershipFormModal from 'src/features/membership/components/forms/UpdateMembershipFormModal';
import { Membership, MEMBERSHIP_CATEGORY, MEMBERSHIP_STATUS } from 'src/features/membership/types';
import { membershipMessages } from 'src/features/membership/utils/messages';
import { Button } from 'src/registry/ui/Button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/registry/ui/Tooltip';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { commonMessages } from 'src/utils/messages';

type MembershipActionButtonsProps = {
    data: Membership;
};

const MembershipActionButtons = ({ data }: MembershipActionButtonsProps) => {
    const { formatMessage } = useIntl();

    const membershipCanBeConverted =
        data?.membership_type.category !== MEMBERSHIP_CATEGORY.RESERVATION &&
        data?.membership_type.category !== MEMBERSHIP_CATEGORY.SUBSCRIPTION;
    const isExpired = data?.status === MEMBERSHIP_STATUS.INACTIVE;
    const isMembershipActiveOrExpired = data?.status !== MEMBERSHIP_STATUS.UPCOMING;

    return (
        <div className="flex flex-row gap-2">
            {membershipCanBeConverted && (
                <ConvertMembershipFormModal selectedMembership={data}>
                    <Button size="mdIcon">
                        <FontAwesomeIcon icon={ICON_MAP.CONVERT} />
                        <span className="hidden md:block">{formatMessage(membershipMessages.convertMembership)}</span>
                    </Button>
                </ConvertMembershipFormModal>
            )}
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span className="inline-block">
                            <UpdateMembershipFormModal
                                semantics={{ entryName: formatMessage(membershipMessages.membership).toLowerCase() }}
                                selectedMembership={data}
                            >
                                <Button
                                    size="mdIcon"
                                    variant="outline"
                                    className="cursor-pointer"
                                    disabled={isExpired} //FOR NOW WE ARE ONLY CHECKING IF THE MEMBERSHIP IS EXPIRED FOR EDIT
                                >
                                    <FontAwesomeIcon icon={ICON_MAP.EDIT} />
                                    <span className="hidden md:block">{formatMessage(commonMessages.edit)}</span>
                                </Button>
                            </UpdateMembershipFormModal>
                        </span>
                    </TooltipTrigger>
                    {isExpired && (
                        <TooltipContent side="top">
                            {formatMessage(membershipMessages.expiredMembershipTooltip)}
                        </TooltipContent>
                    )}
                </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span className="inline-block">
                            <DeleteMembershipFormModal
                                semantics={{ entryName: formatMessage(membershipMessages.membership).toLowerCase() }}
                                selectedMembership={data}
                            >
                                <Button
                                    size="mdIcon"
                                    variant="destructiveLight"
                                    className="cursor-pointer"
                                    disabled={isMembershipActiveOrExpired}
                                >
                                    <FontAwesomeIcon icon={ICON_MAP.DELETE} />
                                    <span className="hidden md:block">{formatMessage(generalMessages.delete)}</span>
                                </Button>
                            </DeleteMembershipFormModal>
                        </span>
                    </TooltipTrigger>
                    {isExpired && (
                        <TooltipContent side="top">
                            {formatMessage(membershipMessages.expiredMembershipTooltip)}
                        </TooltipContent>
                    )}
                </Tooltip>
            </TooltipProvider>
        </div>
    );
};

export default MembershipActionButtons;
